import { FormattedMessage, useIntl } from 'react-intl';
import { Modal, Form, Button, notification } from 'antd';
import CustomersReportForm from '../CustomersReportForm';
import { ModalProps } from '@/types/ModalProps';
import {
  useCustomerReportCsvGenerate,
  useGenerateCustomersReport,
  useGenerateCustomersTags,
} from '@/core/domain/reports/people/Report.hooks';
import { useReportGenerate } from '@/hooks';
import {
  CustomersTagsResponse,
  ReportCustomersFormValue,
} from '@/core/domain/reports/people/Report.types';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import generateCsv from '@/utils/functions/generateCsv';
import moment from 'moment';
import { applyCpfCnpjMask } from '@/utils/mask';

export interface Props extends ModalProps {
  hasGenerateTags?: boolean;
  onCallbackGenerateTags?: (customer: CustomersTagsResponse[]) => void;
}

function CustomersReportModal({
  visible,
  handleVisible,
  hasGenerateTags,
  onCallbackGenerateTags,
}: Props) {
  const intl = useIntl();
  const [form] = Form.useForm<ReportCustomersFormValue>();
  const mutation = useGenerateCustomersReport();
  const generateCustomerTags = useGenerateCustomersTags();
  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();
  const report = useReportGenerate();
  const csvReport = useCustomerReportCsvGenerate();

  async function handleGenerateTags() {
    try {
      const { birthdateSpecific, birthDates, ...values } =
        await form.validateFields();
      const activeMap = {
        0: undefined,
        1: true,
        2: false,
      };

      const obj = {
        ...values,
        active: activeMap[values.active],
        fromBirthdayDate: birthDates
          ? birthdateSpecific
            ? birthDates[0].format('YYYY-MM-DD')
            : birthDates[0].format('MM-DD') ?? undefined
          : undefined,
        toBirthdayDate: birthDates
          ? birthdateSpecific
            ? birthDates[1].format('YYYY-MM-DD')
            : birthDates[1].format('MM-DD')
          : undefined,
        fromCreationDate: values.creationDates
          ? values.creationDates[0].format('YYYY-MM-DD')
          : undefined,
        toCreationDate: values.creationDates
          ? values.creationDates[1].format('YYYY-MM-DD')
          : undefined,
      };

      const result = await generateCustomerTags.mutateAsync(obj);

      if (onCallbackGenerateTags) {
        onCallbackGenerateTags(result);
      }

      handleVisible();
    } catch {}
  }

  const handleCsv = async () => {
    try {
      const { birthdateSpecific, birthDates, ...values } =
        await form.validateFields();

      const activeMap = {
        0: undefined,
        1: true,
        2: false,
      };

      const obj = {
        ...values,
        active: activeMap[values.active],
        fromBirthdayDate: birthDates
          ? birthdateSpecific
            ? birthDates[0].format('YYYY-MM-DD')
            : birthDates[0].format('MM-DD') ?? undefined
          : undefined,
        toBirthdayDate: birthDates
          ? birthdateSpecific
            ? birthDates[1].format('YYYY-MM-DD')
            : birthDates[1].format('MM-DD')
          : undefined,
        fromCreationDate: values.creationDates
          ? values.creationDates[0].format('YYYY-MM-DD')
          : undefined,
        toCreationDate: values.creationDates
          ? values.creationDates[1].format('YYYY-MM-DD')
          : undefined,
      };
      const data = await csvReport.mutateAsync(obj);
      const csvData = data.map(
        ({ name, email, address, phone, birthDate, creation, cpfCnpj }) => ({
          ['Nome']: name,
          ['CPF/CNPJ']: applyCpfCnpjMask(cpfCnpj) || '',
          ['Email']: email,
          ['Endereço']: address,
          ['Telefone']: phone,
          ['Aniversário']: birthDate
            ? moment(birthDate).format('DD/MM/YYYY')
            : '',
          ['Data de inclusão']: moment(creation).format('DD/MM/YYYY'),
        }),
      );
      generateCsv(csvData, 'Clientes');
    } catch {}
  };

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const { birthdateSpecific, birthDates, ...values } =
        await form.validateFields();
      const activeMap = {
        0: undefined,
        1: true,
        2: false,
      };

      const obj = {
        ...values,
        format: FILE_FORMAT[format],
        active: activeMap[values.active],
        fromBirthdayDate: birthDates
          ? birthdateSpecific
            ? birthDates[0].format('YYYY-MM-DD')
            : birthDates[0].format('MM-DD') ?? undefined
          : undefined,
        toBirthdayDate: birthDates
          ? birthdateSpecific
            ? birthDates[1].format('YYYY-MM-DD')
            : birthDates[1].format('MM-DD')
          : undefined,
        fromCreationDate: values.creationDates
          ? values.creationDates[0].format('YYYY-MM-DD')
          : undefined,
        toCreationDate: values.creationDates
          ? values.creationDates[1].format('YYYY-MM-DD')
          : undefined,
      };

      const result = await mutation.mutateAsync(obj);

      if (callback) {
        callback(result);
      }

      notification.success({
        message: intl.formatMessage({
          id: 'customers.notification.generate.report.title',
        }),
        description: intl.formatMessage({
          id: 'customers.notification.generate.report.description',
        }),
      });
      handleVisible();
    } catch {}
    setFileUnloading(format);
  };

  return (
    <Modal
      width={800}
      open={visible}
      onCancel={handleVisible}
      title={intl.formatMessage({ id: 'customers.modal.report.title' })}
      footer={
        <SpaceForButton justifyContent="flex-end">
          {!hasGenerateTags ? (
            <>
              <Button
                block
                type="primary"
                onClick={() =>
                  handleGenerateReport('REPORT_VIEW', (result) =>
                    report.openFile({
                      result,
                      fileName: 'relatorio-de-cliente.pdf',
                      fileType: 'application/pdf',
                    }),
                  )
                }
                key="viewReport"
                loading={fileLoading.REPORT_VIEW}
              >
                <FormattedMessage id="customers.modal.report.buttons.viewReport" />
              </Button>
              <Button
                block
                type="primary"
                key="PDF"
                onClick={() =>
                  handleGenerateReport('PDF', (result) =>
                    report.downloadFile({
                      result,
                      fileName: 'relatorio-de-cliente.pdf',
                    }),
                  )
                }
                loading={fileLoading.PDF}
              >
                <FormattedMessage id="customers.modal.report.buttons.pdf" />
              </Button>
              <Button
                block
                type="primary"
                key="CSV"
                onClick={handleCsv}
                loading={csvReport.isLoading}
              >
                <FormattedMessage id="customers.modal.report.buttons.csv" />
              </Button>
            </>
          ) : (
            <Button block type="primary" onClick={handleGenerateTags}>
              Gerar Etiquetas
            </Button>
          )}
        </SpaceForButton>
      }
    >
      <CustomersReportForm form={form} />
    </Modal>
  );
}

export default CustomersReportModal;
